import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { FileEdit, ClipboardCheck, ListChecks } from "lucide-react";
import { useModules } from "@/context/ModulesContext";
import RehearsalReports from "@/pages/RehearsalReports";
import Attendance from "@/pages/Attendance";
import Actionables from "@/pages/Actionables";

const TABS = [
  { id: "reports", label: "Reports", icon: FileEdit, hint: "Wrap-ups, notes & follow-ups filed per rehearsal.", module: "rehearsal_reports" },
  { id: "attendance", label: "Attendance", icon: ClipboardCheck, hint: "Who was in the room for each call.", staffOnly: true },
  { id: "actionables", label: "Actionables", icon: ListChecks, hint: "Every follow-up task from every report.", module: "rehearsal_reports" },
];

/**
 * Rehearsals hub — collapses Reports, Attendance, and Actionables into one
 * sidebar anchor with tabs. Uses `?tab=…` for deep-linking and to keep
 * "New report" quick-action links working (`?tab=reports&new=1`).
 */
export default function Rehearsals() {
  const { user } = useAuth();
  const { modules } = useModules();
  const [params, setParams] = useSearchParams();
  const initial = params.get("tab") || "reports";

  const visibleTabs = useMemo(() => TABS.filter((t) => {
    if (t.staffOnly && user.role === "cast") return false;
    if (t.module && modules?.[t.module] === false) return false;
    return true;
  }), [user.role, modules]);

  const validInitial = visibleTabs.find((t) => t.id === initial)?.id || visibleTabs[0]?.id || "reports";
  const [tab, setTab] = useState(validInitial);

  const setActive = (id) => {
    setTab(id);
    const next = new URLSearchParams(params);
    next.set("tab", id);
    setParams(next, { replace: true });
  };

  const active = visibleTabs.find((t) => t.id === tab) || visibleTabs[0];

  return (
    <div className="space-y-6 animate-fade-up" data-testid="rehearsals-hub">
      <header>
        <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Show</div>
        <h1 className="mt-1 font-display text-4xl tracking-tight font-light">Rehearsals</h1>
        <p className="mt-1 text-zinc-400 text-sm">{active?.hint}</p>
      </header>

      <div className="flex flex-wrap gap-1 border border-white/10 rounded-md bg-[#121212] p-1 w-fit" data-testid="rehearsals-tabs">
        {visibleTabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`rehearsals-tab-${id}`}
            onClick={() => setActive(id)}
            className={`h-9 px-3 rounded text-sm inline-flex items-center gap-1.5 transition-colors
              ${tab === id ? "bg-gold text-black font-semibold" : "text-zinc-300 hover:bg-white/5"}`}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      <div className="pt-2">
        {tab === "reports" && <RehearsalReports embedded />}
        {tab === "attendance" && <Attendance embedded />}
        {tab === "actionables" && <Actionables embedded />}
      </div>
    </div>
  );
}
